/**
 * List view for browsing dances with filtering
 */

import { useState, useMemo } from 'react';
import { useDances } from '../hooks/useDances';
import { DanceCard } from './DanceCard';
import type { DanceSource, DanceDifficulty, DanceStyle, EnergyLevel, ProgramRole } from '../api/danceApi';

export function DanceList() {
  const { data: allDances, isLoading, isError, error } = useDances('ALL');

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<DanceSource | 'ALL' | ''>('');
  const [difficultyFilter, setDifficultyFilter] = useState<DanceDifficulty | ''>('');
  const [styleFilter, setStyleFilter] = useState<DanceStyle | ''>('');
  const [energyFilter, setEnergyFilter] = useState<EnergyLevel | ''>('');
  const [roleFilter, setRoleFilter] = useState<ProgramRole | ''>('');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [selectedMoves, setSelectedMoves] = useState<Set<string>>(new Set());

  // Extract all available tags from dances
  const availableTags = useMemo(() => {
    if (!allDances) return [];
    const tagSet = new Set<string>();
    allDances.forEach(dance => {
      dance.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [allDances]);

  const availableMoves = useMemo(() => {
    if (!allDances) return [];
    const moveSet = new Set<string>();
    allDances.forEach(dance => {
      dance.moves?.forEach(move => moveSet.add(move));
    });
    return Array.from(moveSet).sort();
  }, [allDances]);

  // Filter dances based on all criteria
  const filteredDances = useMemo(() => {
    if (!allDances) return [];

    return allDances.filter(dance => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchFields = [
          dance.title,
          dance.choreographer,
          dance.a1,
          dance.a2,
          dance.b1,
          dance.b2,
          dance.notes
        ];
        const match = searchFields.some(f => f?.toLowerCase().includes(query));
        if (!match) return false;
      }

      if (sourceFilter && sourceFilter !== 'ALL' && dance.source !== sourceFilter) return false;
      if (difficultyFilter && dance.difficulty !== difficultyFilter) return false;
      if (styleFilter && dance.style !== styleFilter) return false;
      if (energyFilter && dance.energyLevel !== energyFilter) return false;
      if (roleFilter && dance.programRole !== roleFilter) return false;

      if (selectedTags.size > 0) {
        const danceTags = new Set(dance.tags || []);
        for (const tag of selectedTags) {
          if (!danceTags.has(tag)) return false;
        }
      }

      if (selectedMoves.size > 0) {
        const danceMoves = new Set(dance.moves || []);
        for (const move of selectedMoves) {
          if (!danceMoves.has(move)) return false;
        }
      }

      return true;
    });
  }, [allDances, searchQuery, sourceFilter, difficultyFilter, styleFilter, energyFilter, roleFilter, selectedTags, selectedMoves]);

  const toggleTag = (tag: string) => {
    const newSelected = new Set(selectedTags);
    if (newSelected.has(tag)) newSelected.delete(tag);
    else newSelected.add(tag);
    setSelectedTags(newSelected);
  };

  const toggleMove = (move: string) => {
    const newSelected = new Set(selectedMoves);
    if (newSelected.has(move)) newSelected.delete(move);
    else newSelected.add(move);
    setSelectedMoves(newSelected);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSourceFilter('');
    setDifficultyFilter('');
    setStyleFilter('');
    setEnergyFilter('');
    setRoleFilter('');
    setSelectedTags(new Set());
    setSelectedMoves(new Set());
  };

  if (isLoading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading dances...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
          Error loading dances: {error.message}
        </div>
      </div>
    );
  }

  const hasActiveFilters = searchQuery || sourceFilter || difficultyFilter || styleFilter ||
    energyFilter || roleFilter || selectedTags.size > 0 || selectedMoves.size > 0;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem' }}>
      <h1 style={{ marginBottom: '1.5rem' }}>Browse Dances</h1>

      {/* Search bar */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Search by title, choreographer, or notation..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
      </div>

      {/* Filter dropdowns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '1rem'
      }}>
        <div>
          <label htmlFor="source" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '600' }}>
            Source
          </label>
          <select
            id="source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as any)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="">All</option>
            <option value="AI">AI-Generated</option>
            <option value="MANUAL">Manual</option>
          </select>
        </div>

        <div>
          <label htmlFor="difficulty" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '600' }}>
            Difficulty
          </label>
          <select
            id="difficulty"
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value as any)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="">All</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <div>
          <label htmlFor="style" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '600' }}>
            Style
          </label>
          <select
            id="style"
            value={styleFilter}
            onChange={(e) => setStyleFilter(e.target.value as any)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="">All</option>
            <option value="contra">Contra</option>
            <option value="square">Square</option>
            <option value="english-country">English Country</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="energy" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '600' }}>
            Energy
          </label>
          <select
            id="energy"
            value={energyFilter}
            onChange={(e) => setEnergyFilter(e.target.value as any)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="">All</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div>
          <label htmlFor="role" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '600' }}>
            Role
          </label>
          <select
            id="role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as any)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="">All</option>
            <option value="opener">Opener</option>
            <option value="filler">Filler</option>
            <option value="closer">Closer</option>
          </select>
        </div>
      </div>

      {/* Tag filter chips */}
      {availableTags.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Tags:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {availableTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                style={{
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #007bff',
                  borderRadius: '16px',
                  backgroundColor: selectedTags.has(tag) ? '#007bff' : 'white',
                  color: selectedTags.has(tag) ? 'white' : '#007bff',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Move filter chips */}
      {availableMoves.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Moves:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {availableMoves.map(move => (
              <button
                key={move}
                type="button"
                onClick={() => toggleMove(move)}
                style={{
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #28a745',
                  borderRadius: '16px',
                  backgroundColor: selectedMoves.has(move) ? '#28a745' : 'white',
                  color: selectedMoves.has(move) ? 'white' : '#28a745',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {move}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results summary and clear filters */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        padding: '0.75rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <div style={{ fontWeight: '600' }}>
          Showing {filteredDances.length} of {allDances?.length || 0} dances
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Dance list */}
      {filteredDances.length > 0 ? (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {filteredDances.map((dance) => (
            <DanceCard key={dance.danceId} dance={dance} />
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
          {hasActiveFilters ? (
            <>
              <p style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-md)' }}>No dances match your filters.</p>
              <button
                type="button"
                onClick={clearFilters}
                className="btn btn-primary"
              >
                Clear All Filters
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-md)' }}>No dances yet!</p>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="/generate" className="btn" style={{ backgroundColor: 'var(--color-success)', color: 'white', textDecoration: 'none' }}>Generate with AI</a>
                <a href="/upload" className="btn btn-primary" style={{ textDecoration: 'none' }}>Upload Manually</a>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
